import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchBackendVersion } from '../../api/backendVersionClient';
import { fetchCurrentLicense } from '../../api/currentLicenseClient';
import { useAuthStore } from '../../stores/useAuthStore';
import { usePluginUpdateStore } from '../../stores/usePluginUpdateStore';
import { borderRadius } from '../../../theme/theme';

type BackendVersionState =
  | { status: 'idle' | 'loading' | 'failed'; version: '' }
  | { status: 'ready'; version: string };

type CurrentLicenseState =
  | {
      status: 'idle' | 'loading' | 'failed';
      dueTime: null;
      isDemo: null;
      remainingExportElements: null;
    }
  | {
      status: 'ready';
      dueTime: string | null;
      isDemo: boolean;
      remainingExportElements: number | null;
    };

interface VersionRowProps {
  label: string;
  value: string;
  testId: string;
}

const VersionRow: React.FC<VersionRowProps> = ({ label, value, testId }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(96px, auto) minmax(0, 1fr)',
      alignItems: 'baseline',
      gap: 2,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontFamily="monospace"
      fontWeight={600}
      textAlign="right"
      sx={{ overflowWrap: 'anywhere' }}
      data-testid={testId}
      aria-live="polite"
    >
      {value}
    </Typography>
  </Box>
);

function useBackendVersion(open: boolean) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<BackendVersionState>({
    status: 'idle',
    version: '',
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let active = true;
    setState({ status: 'loading', version: '' });

    void fetchBackendVersion()
      .then((version) => {
        if (active) {
          setState({ status: 'ready', version });
        }
      })
      .catch(() => {
        if (active) {
          setState({ status: 'failed', version: '' });
        }
      });

    return () => {
      active = false;
    };
  }, [attempt, open]);

  const retry = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  return { ...state, retry };
}

const EmptyLicenseFields = {
  dueTime: null,
  isDemo: null,
  remainingExportElements: null,
} as const;

function useCurrentLicense(open: boolean) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<CurrentLicenseState>({
    status: 'idle',
    ...EmptyLicenseFields,
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let active = true;
    setState({ status: 'loading', ...EmptyLicenseFields });

    void fetchCurrentLicense()
      .then((license) => {
        if (active) {
          setState({
            status: 'ready',
            dueTime: license.dueTime,
            isDemo: license.isDemo,
            remainingExportElements: license.remainingExportElements,
          });
        }
      })
      .catch(() => {
        if (active) {
          setState({ status: 'failed', ...EmptyLicenseFields });
        }
      });

    return () => {
      active = false;
    };
  }, [attempt, open]);

  const retry = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  return { ...state, retry };
}

const MsPerUtcDay = 24 * 60 * 60 * 1000;

/** Whole UTC calendar days from `now` until `dueTime`; negative once the date is past. */
export const wholeUtcDaysLeft = (dueTime: string, now: Date): number =>
  Math.floor(Date.parse(dueTime) / MsPerUtcDay) - Math.floor(now.getTime() / MsPerUtcDay);

export const formatLicenseExpiry = (dueTime: string | null, now: Date): string => {
  if (!dueTime) {
    return 'No expiration';
  }

  const date = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(dueTime));
  const daysLeft = wholeUtcDaysLeft(dueTime, now);

  if (daysLeft < 0) {
    return `${date} (expired)`;
  }
  if (daysLeft === 0) {
    return `${date} (expires today)`;
  }
  return `${date} (${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left)`;
};

/** Demo licenses show the numeric remaining PCF-export count; anything unlimited shows "∞". */
export const formatExportsLeft = (
  isDemo: boolean,
  remainingExportElements: number | null
): string =>
  isDemo && remainingExportElements !== null ? String(remainingExportElements) : '∞';

const licenseRowValue = (
  license: CurrentLicenseState,
  format: (license: Extract<CurrentLicenseState, { status: 'ready' }>) => string
): string => {
  if (license.status === 'failed') {
    return 'Unavailable';
  }
  if (license.status !== 'ready') {
    return 'Loading…';
  }
  return format(license);
};

const OpenRuntimeVersionInfoEvent = 'pipeex:open-runtime-version-info';

export const openRuntimeVersionInfo = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OpenRuntimeVersionInfoEvent));
  }
};

export const RuntimeVersionButton: React.FC = () => (
  <Tooltip title="Versions">
    <IconButton
      size="small"
      onClick={openRuntimeVersionInfo}
      aria-label="Open version information"
      data-testid="runtime-version-button"
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        p: 0.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${borderRadius.sm}px`,
        color: 'text.secondary',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 13 }} />
    </IconButton>
  </Tooltip>
);

interface AccountSectionProps {
  username: string;
  role: string;
  license: CurrentLicenseState;
  now: () => Date;
}

const AccountSection: React.FC<AccountSectionProps> = ({ username, role, license, now }) => (
  <>
    <Typography variant="subtitle2">Account</Typography>
    <VersionRow label="User" value={username} testId="account-username-value" />
    <VersionRow label="Role" value={role} testId="account-role-value" />
    <VersionRow
      label="License expires"
      value={licenseRowValue(license, (ready) => formatLicenseExpiry(ready.dueTime, now()))}
      testId="license-expiry-value"
    />
    <VersionRow
      label="Exports left"
      value={licenseRowValue(license, (ready) =>
        formatExportsLeft(ready.isDemo, ready.remainingExportElements)
      )}
      testId="license-exports-left-value"
    />
    {license.status === 'failed' ? (
      <Alert severity="warning">License information unavailable.</Alert>
    ) : null}
  </>
);

interface RuntimeSectionProps {
  pluginVersion: string;
  latestPluginVersion: string;
  backendVersion: string;
  updateAvailable: boolean;
  backendFailed: boolean;
}

const RuntimeSection: React.FC<RuntimeSectionProps> = ({
  pluginVersion,
  latestPluginVersion,
  backendVersion,
  updateAvailable,
  backendFailed,
}) => (
  <>
    <Divider />
    <Typography variant="subtitle2">Runtime</Typography>
    <VersionRow label="Plugin" value={pluginVersion} testId="plugin-version-value" />
    {latestPluginVersion ? (
      <VersionRow
        label="Latest plugin"
        value={latestPluginVersion}
        testId="plugin-latest-version"
      />
    ) : null}
    <VersionRow label="Backend" value={backendVersion} testId="backend-version-value" />
    {updateAvailable ? (
      <Alert severity="info">Plugin {latestPluginVersion} is available.</Alert>
    ) : null}
    {backendFailed ? (
      <Alert severity="warning">Backend version unavailable.</Alert>
    ) : null}
  </>
);

interface RuntimeVersionInfoProps {
  /** Clock override so tests can pin "now"; defaults to the real time. */
  now?: () => Date;
}

/** App-level dialog opened by either the web or native upper toolbar. */
const RuntimeVersionInfo: React.FC<RuntimeVersionInfoProps> = ({
  now = () => new Date(),
}) => {
  const [open, setOpen] = useState(false);
  const backend = useBackendVersion(open);
  const currentLicense = useCurrentLicense(open);
  const user = useAuthStore((state) => state.user);
  const pluginState = usePluginUpdateStore((state) => state.state);
  const installedPluginVersion = usePluginUpdateStore(
    (state) => state.installedVersion
  );
  const latestPluginVersion = usePluginUpdateStore((state) => state.latestVersion);
  const pluginInitialized = usePluginUpdateStore((state) => state.initialized);
  const pluginChecking = usePluginUpdateStore((state) => state.isChecking);
  const ensurePluginChecked = usePluginUpdateStore((state) => state.ensureChecked);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(OpenRuntimeVersionInfoEvent, handleOpen);
    return () => window.removeEventListener(OpenRuntimeVersionInfoEvent, handleOpen);
  }, []);

  useEffect(() => {
    if (open) {
      void ensurePluginChecked();
    }
  }, [ensurePluginChecked, open]);

  const pluginVersion = installedPluginVersion ||
    (!pluginInitialized || pluginChecking ? 'Loading…' : 'Unavailable');
  const backendVersion = backend.status === 'ready'
    ? backend.version
    : backend.status === 'failed'
      ? 'Unavailable'
      : 'Loading…';
  const updateAvailable =
    pluginState === 'UPDATE_AVAILABLE' && Boolean(latestPluginVersion);
  const retryFailed = () => {
    if (backend.status === 'failed') {
      backend.retry();
    }
    if (currentLicense.status === 'failed') {
      currentLicense.retry();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="runtime-version-title"
      fullWidth
      maxWidth="xs"
      data-testid="runtime-version-dialog"
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      <DialogTitle id="runtime-version-title">PipeEX versions</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <AccountSection
            username={user?.username ?? 'Unavailable'}
            role={user?.role ?? 'Unavailable'}
            license={currentLicense}
            now={now}
          />
          <RuntimeSection
            pluginVersion={pluginVersion}
            latestPluginVersion={latestPluginVersion}
            backendVersion={backendVersion}
            updateAvailable={updateAvailable}
            backendFailed={backend.status === 'failed'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {backend.status === 'failed' || currentLicense.status === 'failed' ? (
          <Button onClick={retryFailed}>Retry</Button>
        ) : null}
        <Button onClick={() => setOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuntimeVersionInfo;
